import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import * as toTitleCase from 'title-case';
import { Modal, message, Tag } from 'antd';
import HorizontalScroll from '../../../components/horizontalScroll/index';

import EditUser from '../../Modals/EditUser';
import unblockUser from './graphql';
import './ViewUsersGrid.scss';
import Actions from './subComponents/actions';
import msgs from '../../../config/messages';
import {
  requestPassword,
  deleteUser,
  UserForApproveUser,
} from '../../../graphql/users';
import {
  createUser,
} from '../../../graphql/updateUsers';
import { getOrgAdmins } from '../../../graphql/organizationDetails';
import { filterMethod, otherFilter, setFilterPlaceholders, tooltipColumn, tooltipRow } from '../filterFunctions';

const confirm = Modal.confirm;

export default class ViewUsersGrid extends React.PureComponent {
  static propTypes = {
    users: PropTypes.array,
    refreshUsers: PropTypes.func.isRequired,
    organizationsStatus: PropTypes.string.isRequired,
    organizationsName: PropTypes.string.isRequired,
    organizationsCage: PropTypes.string.isRequired,
    auth: PropTypes.shape({
      jwtToken: PropTypes.string.isRequired,
      expiration: PropTypes.number.isRequired,
      organizationId: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
    }).isRequired,
  };

  static defaultProps = {
    users: [],
  };

  static initialState = {
    noUsers: false,
    selectedUserId: undefined,
    showEditModal: false,
  };

  componentDidMount() {
    setFilterPlaceholders();
  }

  componentDidUpdate() {
    setFilterPlaceholders();
  }

  state = ViewUsersGrid.initialState;

  formatUsers(users) {
    const { organizationsName, organizationsCage } = this.props;
    if (users[0] !== null) {
      return users.map(user => ({
        ...user,
        name: toTitleCase(user.name),
        role: toTitleCase(user.role),
        phone: user.phoneNumber || user.phone || '',
        iconStatus: toTitleCase(user.iconStatus),
        organizationsName: user.orgName || organizationsName || '',
        organizationsCage: user.cageCode || organizationsCage || '',
      }));
    }
    return [];
  }

  _deleteUser = async (
    id,
    name,
    organizationId = this.props.auth.organizationId,
  ) => {
    const self = this;
    const {
      organization: { admins },
    } = await getOrgAdmins(organizationId);
    if (admins.length === 1 && admins[0].id === id) {
      confirm({
        title: 'You cannot do that',
        content: 'Last admin cannot be deleted',
      });
    } else {
      confirm({
        title: `Are you sure?`,
        content: `You are about to delete ${name} from your organization. 
        This action cannot be undone`,
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'No',
        async onOk() {
          await deleteUser(id);
          self.props.refreshUsers();
        },
      });
    }
  };

  requestUserResetPassword = async id => {
    const res = await requestPassword(id);
    if (res.requestPasswordReset && res.requestPasswordReset.id) {
      message.success('successfully sent');
    } else {
      message.error('failed to send email');
    }
  };

  unblockUser = async id => {
    try {
      await unblockUser(id);
      message.success('Successfully Unblocked User');
    } catch (error) {
      message.error('Something went wrong! Please try again');
    }
    this.props.refreshUsers();
  };

  approveUser = async original => {
    let response = await UserForApproveUser(original.id);
    const user = response.user;
    switch (user.role.toUpperCase()) {
      case 'COMMON_USER':
      case 'GOVERNMENT_USER':
        user.role = 'user';
        break;
      case 'COMMON_ADMIN':
        user.role = 'admin';
        break;
      case 'SUPER_ADMIN':
        user.role = 'super';
        break;
      default:
        user.role = 'user';
        break;
    }
    user.iconStatus = 'approved';
    response = await createUser(user);
    await this.props.refreshUsers();
  };

  closeEditModal = () => {
    this.setState({
      showEditModal: false,
      selectedUserId: undefined,
    });
  };

  openEditModal = (itemId) => {
    this.setState({
      showEditModal: true,
      selectedUserId: itemId,
    });
  };

  render() {
    const { users, organizationsStatus } = this.props;

    const columns = [
      {
        Header: 'Full Name',
        accessor: 'name',
        minWidth: 140,
        maxWidth: 200,
      },
      {
        Header: 'Email',
        accessor: 'email',
        minWidth: 200,
        maxWidth: 280,
      },
      {
        Header: 'Phone Number',
        accessor: 'phone',
        minWidth: 120,
        maxWidth: 160,
      },
      {
        Header: 'Organization Name',
        accessor: 'organizationsName',
        minWidth: 180,
        maxWidth: 280,
      },
      {
        Header: 'CAGE',
        accessor: 'organizationsCage',
        minWidth: 80,
        maxWidth: 120,
      },
      {
        Header: 'Account Status',
        accessor: 'iconStatus',
        minWidth: 110,
        maxWidth: 150,
      },
      {
        Header: 'Role',
        accessor: 'role',
        minWidth: 100,
        maxWidth: 140,
      },
      {
        Header: 'Action',
        accessor: 'action',
        minWidth: 200,
        maxWidth: 280,
        className: 'action-cell',
        filterMethod: otherFilter,
        Cell: cellProps => {
          if (
            organizationsStatus === 'approved' ||
            cellProps.original.orgStatus === 'approved'
          ) {
            return (
              <Actions
                unblockUser={this.unblockUser}
                cellProps={cellProps}
                deleteUser={this._deleteUser}
                approveUser={this.approveUser}
                requestUserResetPassword={this.requestUserResetPassword}
                openEditModal={this.openEditModal}
              />
            );
          }
          return <Tag>Organization must be approved</Tag>;
        },
      },
    ];

    const pageSize = 20;
    return (
      <div className="nf-view-users-grid">
        <div className="responsive-table-container">
          <HorizontalScroll>
            <div style={{ width: '100%' }}>
              <ReactTable
                data={this.formatUsers(users)}
                columns={columns}
                showPageSizeOptions={1}
                showPagination
                noDataText={msgs.ViewUsersGrid.noDataText}
                defaultPageSize={pageSize}
                className="-striped -highlight"
                // minRows={5}
                resizable
                style={{
                  width: '100%',
                  height: '100%'
                }}
                getTableProps={() => ({
                  style: {
                    width: '100%',
                    tableLayout: 'fixed'
                  }
                })}
                pageSizeOptions={[10, 20, 25, 50]}
                defaultSortMethod={(a, b) => {
                  if (a === b) return 0;
                  return a < b ? -1 : 1;
                }}
                // filterable
                defaultFilterMethod={filterMethod}
                getTheadFilterProps={() => ({
                  style: {
                    color: '#f00'
                  },
                })}
                getTheadFilterThProps={(state, rowInfo, column) =>
                ({
                  title: column.Header === 'Action' ? tooltipRow : tooltipColumn,
                })
                }
              />
            </div>
          </HorizontalScroll>
        </div>
        {this.state.showEditModal && this.state.selectedUserId &&
          <EditUser
            selectedUserId={this.state.selectedUserId}
            showEditModal={this.state.showEditModal}
            auth={this.props.auth}
            closeEditModal={this.closeEditModal}
            refreshUsers={this.props.refreshUsers}
          />
        }
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/grids/ViewUsersGrid/ViewUsersGrid.jsx